// ─── Third Party Libraries ─────────────────────────────────────
import classNames from "classnames";

// ─── React & Hooks ─────────────────────────────────────────────
import { useCallback, useState } from "react";

// ─── Components ────────────────────────────────────────────────
import WalletCard from "../walletCard";

// ─── Hooks & Providers ────────────────────────────────────────

// ─── Styles ───────────────────────────────────────────────────
import styles from "./styles.module.scss";
import { formatAmount } from "../../../../setup/util/wallet";
import WalletModal from "../walletModal";
import { useWallet } from "../../../../providers/wallet/wallet.provider";
import ManualBalanceCard from "../manualBalanceCard";

// ─── Types & Interfaces ───────────────────────────────────────
type ModalType = "Total Transferred" | "Pending Transfer" | "Open Balance" | "";

interface WalletHeaderCardsProps {
  hideWithdrawCard?: boolean;
}

/**
 * WalletHeaderCards Component
 *
 * Displays the main wallet header section with balance cards and manual withdraw functionality.
 *
 * Features:
 * - Three main balance cards (Open Balance, Pending Transfer, Total Transferred)
 * - Manual withdraw enable/disable card based on current status
 * - Modal management for different wallet views
 * - Responsive layout with left and right sections
 * - Amount formatting with proper decimal places and comma separators
 */
const WalletHeaderCards = ({ hideWithdrawCard }: WalletHeaderCardsProps) => {
  // ─── State Management ─────────────────────────────────────────

  /** Controls which wallet modal is currently open */
  const [modal, setModal] = useState<ModalType>("");

  // ─── Wallet State ────────────────────────────────────────────

  /** Total amount available for withdrawal */
  const cashOutAmount = useWallet((state) =>
    hideWithdrawCard ? state.totalCashOutAmount : state.cashOutAmount
  );

  /** Amount currently being processed/transferred */
  const inProgressAmount = useWallet((state) =>
    hideWithdrawCard ? state.totalInProgressAmount : state.inProgressAmount
  );

  /** Amount in open transactions (not yet completed) */
  const openedAmount = useWallet((state) =>
    hideWithdrawCard ? state.totalOpenedAmount : state.openedAmount
  );

  /** Loading state for wallet data */
  const loading = useWallet((state) => state.loading);

  // ─── Event Handlers ──────────────────────────────────────────

  /**
   * Opens a specific wallet modal based on the card type
   *
   * @param modalType - The type of modal to open
   */
  const openWalletModal = useCallback((modalType: ModalType) => {
    setModal(modalType);
  }, []);

  /**
   * Closes the currently open wallet modal
   */
  const closeWalletModal = useCallback(() => {
    setModal("");
  }, []);

  // ─── Effects ──────────────────────────────────────────────────

  // ─── Render ──────────────────────────────────────────────────
  return (
    <>
      {/* Main Cards Container */}
      <div className={classNames(styles["cards_container"])}>
        {/* Right Section - Manual Withdraw Cards */}

        {!hideWithdrawCard && (
          <div className={styles["right_section"]}>
            <ManualBalanceCard />
          </div>
        )}

        {/* Left Section - Balance Cards */}
        <div
          className={classNames(styles["left_section"], {
            [styles["left_section_disabled"]]: hideWithdrawCard,
          })}>
          {/* Open Balance Card - Top Position */}
          <WalletCard
            onClick={() => openWalletModal("Open Balance")}
            amount={formatAmount(openedAmount)}
            type={"Open Balance"}
            loading={loading}
          />

          {/* Sub Cards Container - Bottom Position */}
          {hideWithdrawCard ? (
            <>
              {/* Pending Transfer Card */}
              <WalletCard
                amount={formatAmount(inProgressAmount)}
                type={"Pending Transfer"}
                onClick={() => openWalletModal("Pending Transfer")}
                loading={loading}
              />

              {/* Total Transferred Card */}
              <WalletCard
                amount={formatAmount(cashOutAmount)}
                type={"Total Transferred"}
                onClick={() => openWalletModal("Total Transferred")}
                loading={loading}
                fourCardLayout={!hideWithdrawCard}
              />
            </>
          ) : (
            <div className={styles["sub_cards_cont"]}>
              {/* Pending Transfer Card */}
              <WalletCard
                amount={formatAmount(inProgressAmount)}
                type={"Pending Transfer"}
                onClick={() => openWalletModal("Pending Transfer")}
                loading={loading}
              />

              {/* Total Transferred Card */}
              <WalletCard
                amount={formatAmount(cashOutAmount)}
                type={"Total Transferred"}
                onClick={() => openWalletModal("Total Transferred")}
                loading={loading}
                fourCardLayout={!hideWithdrawCard}
              />
            </div>
          )}
        </div>
      </div>

      {/* Wallet Detail Modal */}
      <WalletModal type={modal} onClose={closeWalletModal} />
    </>
  );
};

export default WalletHeaderCards;
